import React, { useContext, useEffect } from 'react';
import { Navigate, Outlet, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { SLICES } from '../constants/slices';
import { CSPUrlsMap } from './CSPUrlsMap';
import { FeatureFlagContext } from '../contextProviders/featureFlagContext';
import { UnavailablePage } from '../components/shared/UnavailablePage/UnavailablePage';
import { UNAVAILABLE_PAGE } from '../constants/constants';
import Loading from '../components/shared/Loading/Loading';

const ProtectedRoute = () => {
  const parentOutletContext = useOutletContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { tokenClaims, authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const isAuthenticated = authDetails?.cspAccessToken;
  const { cspConfiguration, isCspConfigLoading } = useContext(FeatureFlagContext);
  const isNotificationPreferencesEnabled = cspConfiguration?.IsNotificationPreferencesEnabled;
  const isLiteModeEnabled = cspConfiguration?.IsLiteModeEnabled;
  const isLiveVetEnabled = cspConfiguration?.IsLiveVetEnabled;
  const isRenewalHubEnabled = cspConfiguration?.IsRenewalHubEnabled;

  useEffect(() => {
    const tokenExpiryInSeconds = tokenClaims?.tokenExpiry;
    const expirationDate = new Date(tokenExpiryInSeconds * 1000);
    const now = Date.now();
    const isAuth0TokenExpired = expirationDate.getTime() < now;
    if (isAuth0TokenExpired) {
      sessionStorage.clear();
      localStorage.clear();
      navigate(CSPUrlsMap.login.url, { replace: true });
    }
  }, [tokenClaims?.tokenExpiry, navigate]);

  const showVerificationEmail =
    tokenClaims?.registrationInProgress &&
    tokenClaims?.verificationPendingEmail &&
    isAuthenticated &&
    !tokenClaims?.mfaSkip;
  const showVerificationSms =
    (isAuthenticated &&
      tokenClaims?.registrationInProgress &&
      tokenClaims?.verificationPendingPhone &&
      !tokenClaims?.verificationPendingEmail &&
      !tokenClaims?.mfaSkip) ||
    (isAuthenticated &&
      tokenClaims?.registrationInProgress &&
      !tokenClaims?.verificationPendingPhone &&
      !tokenClaims?.verificationPendingEmail &&
      !tokenClaims?.mfaSkip) ||
    (isAuthenticated &&
      !tokenClaims?.registrationInProgress &&
      tokenClaims?.verificationPendingPhone &&
      !tokenClaims?.verificationPendingEmail &&
      !tokenClaims?.mfaSkip) ||
    (tokenClaims?.registrationInProgress && tokenClaims?.migratedUser);

  // Lite Mode allowed paths
  const liteModeAllowedPaths = [
    CSPUrlsMap.dashboard.url,
    CSPUrlsMap.helpCentre.url,
    CSPUrlsMap.logout.url,
    CSPUrlsMap.contactUs.url,
    ...(isLiveVetEnabled ? [CSPUrlsMap.liveVet.url] : [])
  ];

  const isUnavailablePath =
    (isLiteModeEnabled === true && !liteModeAllowedPaths.includes(location.pathname)) ||
    (!isNotificationPreferencesEnabled && location.pathname === CSPUrlsMap.notificationPreferences.url) ||
    (!isLiveVetEnabled && location.pathname === CSPUrlsMap.liveVet.url) ||
    (!isRenewalHubEnabled && location.pathname === CSPUrlsMap.renewalHub.url) ||
    (!isRenewalHubEnabled && location.pathname === CSPUrlsMap.renewalHubConfirmation.url);

  if (!isAuthenticated && isCspConfigLoading) {
    return <Loading />;
  }

  if (isUnavailablePath) {
    return (
      <UnavailablePage
        title={UNAVAILABLE_PAGE.TITLE}
        message={UNAVAILABLE_PAGE.MESSAGE}
      />
    );
  }

  if (!tokenClaims) {
    return (
      <Navigate
        to={CSPUrlsMap.login.url}
        replace
      />
    );
  }

  // If not auth0 authenticated and litemode enabled, redirect to login
  if (!authDetails.auth0AccessToken && isLiteModeEnabled === true) {
    return (
      <Navigate
        to={CSPUrlsMap.login.url}
        replace
      />
    );
  }

  // If not csp authenticated and not litemode enabled, redirect to login
  if (!authDetails.cspAccessToken && isLiteModeEnabled === false) {
    return (
      <Navigate
        to={CSPUrlsMap.login.url}
        replace
      />
    );
  }

  // If email verification is true, redirect to email verification page
  if (showVerificationEmail) {
    return (
      <Navigate
        to={CSPUrlsMap.verifyEmail.url}
        replace
      />
    );
  }

  // If email verification is true, redirect to sms verification page
  if (showVerificationSms) {
    return (
      <Navigate
        to={CSPUrlsMap.verifySms.url}
        replace
      />
    );
  }

  return <Outlet context={parentOutletContext} />;
};

export default ProtectedRoute;
